// Web Push in the page (docs/web-konzept.md, section 5.4): the service worker, the subscription
// with the server's VAPID key, and whether this browser may have it at all (only when trusted:
// decision 8/9, a browser that forgets the session should not keep receiving pushes).

import { ApiError } from "../api/client.ts";
import type { WebPushKeyResponse } from "../api/contract.gen.ts";
import type { Session } from "../auth/session.ts";

export type PushState = "unsupported" | "untrusted" | "unconfigured" | "denied" | "off" | "on";

export function supported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register("/sw.js", { scope: "/" });
}

export async function state(session: Session): Promise<PushState> {
  if (!supported()) return "unsupported";
  if (!session.trusted) return "untrusted";
  if (Notification.permission === "denied") return "denied";
  const subscription = await (await registration()).pushManager.getSubscription();
  return subscription ? "on" : "off";
}

/** Asks for permission (the browser's prompt) and subscribes; the server stores it for this device. */
export async function enable(session: Session): Promise<PushState> {
  if (!supported()) return "unsupported";
  if (!session.trusted) return "untrusted";
  let key: string;
  try {
    key = (await session.call<WebPushKeyResponse>("/v1/me/web-push/key")).public_key;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return "unconfigured";
    throw e;
  }
  if ((await Notification.requestPermission()) !== "granted") return Notification.permission === "denied" ? "denied" : "off";
  const reg = await registration();
  const subscription = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: fromBase64Url(key) }));
  const json = subscription.toJSON();
  await session.call("/v1/me/web-push", { method: "PUT", json: { endpoint: json.endpoint, keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth } } });
  return "on";
}

export async function disable(session: Session | null): Promise<PushState> {
  if (!supported()) return "unsupported";
  const reg = await navigator.serviceWorker.getRegistration("/");
  const subscription = await reg?.pushManager.getSubscription();
  if (subscription) {
    if (session?.current) await session.call("/v1/me/web-push", { method: "DELETE", json: { endpoint: subscription.endpoint } }).catch(() => undefined);
    await subscription.unsubscribe().catch(() => undefined);
  }
  return "off";
}

function fromBase64Url(text: string): Uint8Array<ArrayBuffer> {
  const padded = text.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((text.length + 3) % 4);
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}
